/* ══════════════════════════════════════════════════════════════════════
 * dt-backdrop-engine.js — vector backdrop engine + swatch-derived palette
 *
 * Pure, deterministic, no DOM, no deps. Produces well-formed XML SVG that
 * rides the EXISTING svg-layer path:
 *   svgMarkup → setSvgDisplaySize (crisp display) → rasterizeSvgForProduction (300 DPI)
 *
 * Constraints (verified against sanitizeSvg):
 *   - never use `currentColor` (sanitizeSvg forces it to #000000) — explicit hex only
 *   - well-formed XML (quoted attrs, self-closing, xmlns on root)
 *   - deterministic scatter (seeded PRNG) so display + production rasters match
 * ════════════════════════════════════════════════════════════════════ */
(function (root) {
  'use strict';

  /* ---- color helpers ---- */
  function hexToRgb(hex) {
    hex = String(hex || '').replace('#', '').trim();
    if (hex.length === 3) hex = hex.split('').map(function (c) { return c + c; }).join('');
    if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
    return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
  }
  function clamp255(n) { return Math.max(0, Math.min(255, Math.round(n))); }
  function rgbToHex(r, g, b) {
    var h = function (n) { return clamp255(n).toString(16).padStart(2, '0'); };
    return '#' + h(r) + h(g) + h(b);
  }
  function rgbToHls(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    var mx = Math.max(r, g, b), mn = Math.min(r, g, b), h = 0, s = 0, l = (mx + mn) / 2;
    if (mx !== mn) {
      var d = mx - mn;
      s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
      if (mx === r) h = (g - b) / d + (g < b ? 6 : 0);
      else if (mx === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h /= 6;
    }
    return [h, l, s];
  }
  function lum(rgb) { return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]; }

  var BRAND = {
    coral: '#F47E46', forest: '#344E41', ochre: '#C9912F',
    indigo: '#4A5A8A', teal: '#2E6E6A', sage: '#96A88C', cream: '#F3F0E9'
  };
  var FALLBACK_SWATCH = '#C8C8D5'; /* neutral periwinkle if swatch missing/invalid */

  /* Curated brand-palette selection, ranked by harmony + contrast with the
     product swatch. NOT raw HSL complement (that goes garish). */
  function deriveBackdropPalette(swatchHex) {
    var rgb = hexToRgb(swatchHex) || hexToRgb(FALLBACK_SWATCH);
    var hls = rgbToHls(rgb[0], rgb[1], rgb[2]);
    var h = hls[0], pl = lum(rgb);
    var cool = h > 0.45 && h < 0.75;
    var complement = cool ? BRAND.coral : BRAND.teal;   /* warm pop for cool products */
    var contrast = pl > 110 ? BRAND.forest : BRAND.cream; /* dark on light products */
    var tonal = cool ? BRAND.indigo : BRAND.sage;
    var accent = BRAND.ochre;
    var neutral = BRAND.cream;
    /* luminance guard: every fill must be clearly separated from the product */
    function guard(hex) {
      var c = hexToRgb(hex);
      if (Math.abs(lum(c) - pl) >= 38) return hex;
      return rgbToHex(c[0] * 0.7, c[1] * 0.7, c[2] * 0.7);
    }
    return {
      swatch: rgbToHex(rgb[0], rgb[1], rgb[2]),
      complement: guard(complement), contrast: guard(contrast),
      tonal: guard(tonal), accent: guard(accent), neutral: neutral
    };
  }

  /* ---- deterministic PRNG (mulberry32) ---- */
  function rng(seed) {
    return function () {
      seed |= 0; seed = seed + 0x6D2B79F5 | 0;
      var t = Math.imul(seed ^ seed >>> 15, 1 | seed);
      t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }

  /* ---- svg builders ---- */
  function r2(n) { return Math.round(n * 100) / 100; }
  function svgWrap(w, h, inner) {
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + w + ' ' + h + '" width="' + w + '" height="' + h + '">' + inner + '</svg>';
  }
  function rect(x, y, w, h, fill) {
    return '<rect x="' + r2(x) + '" y="' + r2(y) + '" width="' + r2(w) + '" height="' + r2(h) + '" fill="' + fill + '"/>';
  }
  function circle(cx, cy, r, fill, extra) {
    return '<circle cx="' + r2(cx) + '" cy="' + r2(cy) + '" r="' + r2(r) + '" fill="' + fill + '"' + (extra || '') + '/>';
  }
  function blobPath(cx, cy, base) {
    var pts = [], deg, t, rr;
    for (deg = 0; deg < 360; deg += 4) {
      t = deg * Math.PI / 180;
      rr = base * (1 + 0.13 * Math.sin(3 * t + 7) + 0.07 * Math.cos(5 * t));
      pts.push([cx + rr * Math.cos(t), cy + rr * Math.sin(t)]);
    }
    var d = 'M' + r2(pts[0][0]) + ' ' + r2(pts[0][1]);
    for (var i = 1; i < pts.length; i++) d += ' L' + r2(pts[i][0]) + ' ' + r2(pts[i][1]);
    return d + ' Z';
  }

  /* Heart clip path in 0..S space (point at bottom-centre). Used by heart_mask. */
  function heartPath(S) {
    var x = function (f) { return r2(f * S); }, y = function (f) { return r2(f * S); };
    return 'M' + x(0.5) + ' ' + y(0.86) +
      ' C' + x(0.5) + ' ' + y(0.70) + ' ' + x(0.05) + ' ' + y(0.52) + ' ' + x(0.05) + ' ' + y(0.30) +
      ' C' + x(0.05) + ' ' + y(0.10) + ' ' + x(0.35) + ' ' + y(0.07) + ' ' + x(0.5) + ' ' + y(0.27) +
      ' C' + x(0.65) + ' ' + y(0.07) + ' ' + x(0.95) + ' ' + y(0.10) + ' ' + x(0.95) + ' ' + y(0.30) +
      ' C' + x(0.95) + ' ' + y(0.52) + ' ' + x(0.5) + ' ' + y(0.70) + ' ' + x(0.5) + ' ' + y(0.86) + ' Z';
  }

  /* Ellipse + rounded-rect paths in 0..S space (Path2D-parsable: M/A/H/V). */
  function ellipsePath(cx, cy, rx, ryy) {
    return 'M' + r2(cx - rx) + ' ' + r2(cy) +
      ' A' + r2(rx) + ' ' + r2(ryy) + ' 0 1 0 ' + r2(cx + rx) + ' ' + r2(cy) +
      ' A' + r2(rx) + ' ' + r2(ryy) + ' 0 1 0 ' + r2(cx - rx) + ' ' + r2(cy) + ' Z';
  }
  function roundRectPath(x, y, w, h, r) {
    return 'M' + r2(x + r) + ' ' + r2(y) +
      ' H' + r2(x + w - r) + ' A' + r2(r) + ' ' + r2(r) + ' 0 0 1 ' + r2(x + w) + ' ' + r2(y + r) +
      ' V' + r2(y + h - r) + ' A' + r2(r) + ' ' + r2(r) + ' 0 0 1 ' + r2(x + w - r) + ' ' + r2(y + h) +
      ' H' + r2(x + r) + ' A' + r2(r) + ' ' + r2(r) + ' 0 0 1 ' + r2(x) + ' ' + r2(y + h - r) +
      ' V' + r2(y + r) + ' A' + r2(r) + ' ' + r2(r) + ' 0 0 1 ' + r2(x + r) + ' ' + r2(y) + ' Z';
  }

  /* ---- the 12 generators ----
     each returns { svg, mask } ; mask = clip-shape markup for mask backdrops (Phase 2), else null */
  var GEN = {
    transparent: function (P, S) { return { svg: svgWrap(S, S, ''), mask: null }; },
    disc_pop: function (P, S) {
      return { svg: svgWrap(S, S, circle(S / 2, S / 2 + S * 0.06, S * 0.32, P.complement)), mask: null,
        content: { x: 0.18, y: 0.24, w: 0.64, h: 0.64 } };
    },
    disc_mask: function (P, S) {
      var r = S * 0.43;
      var inner = circle(S / 2, S / 2, r, P.contrast) +
        circle(S / 2, S / 2, r, 'none', ' stroke="' + P.complement + '" stroke-width="' + r2(S * 0.025) + '"');
      return { svg: svgWrap(S, S, inner), mask: '<circle cx="' + r2(S / 2) + '" cy="' + r2(S / 2) + '" r="' + r2(r) + '"/>' };
    },
    block: function (P, S) { return { svg: svgWrap(S, S, rect(0, 0, S, S, P.contrast)), mask: null }; },
    sunburst: function (P, S) {
      var n = 24, cx = S / 2, cy = S / 2, R = S * 0.85, inner = '', i, a0, a1;
      for (i = 0; i < n; i += 2) {
        a0 = i * 2 * Math.PI / n; a1 = (i + 1) * 2 * Math.PI / n;
        inner += '<path d="M' + r2(cx) + ' ' + r2(cy) + ' L' + r2(cx + R * Math.cos(a0)) + ' ' + r2(cy + R * Math.sin(a0)) +
          ' A' + r2(R) + ' ' + r2(R) + ' 0 0 1 ' + r2(cx + R * Math.cos(a1)) + ' ' + r2(cy + R * Math.sin(a1)) + ' Z" fill="' + P.complement + '"/>';
      }
      return { svg: svgWrap(S, S, inner), mask: null };
    },
    arch: function (P, S) {
      var inner = '';
      inner += '<path d="M' + r2(S * 0.16) + ' ' + r2(S * 0.86) + ' L' + r2(S * 0.16) + ' ' + r2(S * 0.44) +
        ' A' + r2(S * 0.34) + ' ' + r2(S * 0.34) + ' 0 0 1 ' + r2(S * 0.84) + ' ' + r2(S * 0.44) +
        ' L' + r2(S * 0.84) + ' ' + r2(S * 0.86) + ' Z" fill="' + P.contrast + '"/>';
      return { svg: svgWrap(S, S, inner), mask: null };
    },
    confetti: function (P, S) {
      var rand = rng(9), cols = [P.complement, P.contrast, P.accent, P.tonal], inner = '', i, x, y, w;
      for (i = 0; i < 42; i++) { x = rand() * S; y = rand() * S; w = 6 + rand() * 7; inner += rect(x, y, w, w * 0.5, cols[Math.floor(rand() * 4)]); }
      return { svg: svgWrap(S, S, inner), mask: null };
    },
    dots: function (P, S) {
      var inner = '', step = S * 0.153, gx, gy;
      for (gy = S * 0.073; gy < S; gy += step) for (gx = S * 0.073; gx < S; gx += step) inner += circle(gx, gy, S * 0.03, P.complement);
      return { svg: svgWrap(S, S, inner), mask: null };
    },
    stripes: function (P, S) {
      var inner = '', w = S * 0.066, k;
      for (k = -S; k < S; k += S * 0.133)
        inner += '<path d="M' + r2(k) + ' 0 L' + r2(k + w) + ' 0 L' + r2(k + w + S) + ' ' + S + ' L' + r2(k + S) + ' ' + S + ' Z" fill="' + P.complement + '"/>';
      return { svg: svgWrap(S, S, inner), mask: null };
    },
    doodle: function (P, S) {
      var inner = '';
      inner += circle(S / 2, S / 2, S * 0.4, 'none', ' stroke="' + P.contrast + '" stroke-width="' + r2(S * 0.017) + '"');
      inner += circle(S / 2, S / 2, S * 0.33, 'none', ' stroke="' + P.complement + '" stroke-width="' + r2(S * 0.007) + '"');
      return { svg: svgWrap(S, S, inner), mask: null };
    },
    blob: function (P, S) {
      /* v2.13.5: centred at (S/2, S/2) with base 0.41 so the harmonic
         wobble (±20%) stays inside the 0..S viewBox — the old (S/2−4,
         S/2−2, 0.45) overflowed right (+3.5 px) and top (−0.5 px),
         causing the browser to clip the blob at the viewBox edge. */
      var d = blobPath(S / 2, S / 2, S * 0.41);
      var inner = '<path d="' + d + '" fill="' + P.complement + '"/>' +
        '<path d="' + d + '" fill="none" stroke="' + P.contrast + '" stroke-width="' + r2(S * 0.02) + '"/>';
      return { svg: svgWrap(S, S, inner), mask: '<path d="' + d + '"/>' };
    },
    /* Clean photo-crop masks — transparent backdrop, just the clip shape.
       Not in META (kept out of the user backdrop library); resolved on demand
       by backdrop(key) for the photo templates. */
    circle_mask: function (P, S) {
      return { svg: svgWrap(S, S, ''), mask: '<circle cx="' + r2(S / 2) + '" cy="' + r2(S / 2) + '" r="' + r2(S * 0.47) + '"/>' };
    },
    heart_mask: function (P, S) {
      return { svg: svgWrap(S, S, ''), mask: '<path d="' + heartPath(S) + '"/>' };
    },
    rings: function (P, S) {
      var inner = '', radii = [0.40, 0.30, 0.21, 0.12], cols = [P.complement, P.neutral, P.contrast, P.neutral], i;
      for (i = 0; i < 4; i++) inner += circle(S / 2, S / 2, S * radii[i], cols[i]);
      return { svg: svgWrap(S, S, inner), mask: null };
    },
    /* ── roadmap 1 (June 11): photo-mask frames replacing confetti / block /
       target in the library. Same anatomy as disc_mask: filled shape +
       complement ring, photo clipped to the shape. ── */
    heart_frame: function (P, S) {
      var d = heartPath(S);
      var inner = '<path d="' + d + '" fill="' + P.contrast + '"/>' +
        '<path d="' + d + '" fill="none" stroke="' + P.complement + '" stroke-width="' + r2(S * 0.022) + '"/>';
      return { svg: svgWrap(S, S, inner), mask: '<path d="' + d + '"/>' };
    },
    oval_mask: function (P, S) {
      var d = ellipsePath(S * 0.5, S * 0.5, S * 0.36, S * 0.46);
      var inner = '<path d="' + d + '" fill="' + P.contrast + '"/>' +
        '<path d="' + d + '" fill="none" stroke="' + P.complement + '" stroke-width="' + r2(S * 0.022) + '"/>';
      return { svg: svgWrap(S, S, inner), mask: '<path d="' + d + '"/>' };
    },
    squircle_mask: function (P, S) {
      var d = roundRectPath(S * 0.10, S * 0.10, S * 0.80, S * 0.80, S * 0.16);
      var inner = '<path d="' + d + '" fill="' + P.contrast + '"/>' +
        '<path d="' + d + '" fill="none" stroke="' + P.complement + '" stroke-width="' + r2(S * 0.022) + '"/>';
      return { svg: svgWrap(S, S, inner), mask: '<path d="' + d + '"/>' };
    }
  };

  var META = [
    ['transparent', 'None / transparent', false], ['disc_pop', 'Disc — pop out', false], ['disc_mask', 'Disc — circle mask', true],
    ['oval_mask', 'Oval — cameo frame', true], ['sunburst', 'Sunburst rays', false], ['arch', 'Arch — contrast', false],
    ['heart_frame', 'Heart — photo frame', true], ['dots', 'Polka dots', false], ['stripes', 'Diagonal stripes', false],
    ['doodle', 'Doodle ring', false], ['blob', 'Organic blob', true], ['squircle_mask', 'Rounded square — frame', true]
  ];

  function listBackdrops() { return META.map(function (m) { return { key: m[0], label: m[1], isMask: m[2] }; }); }
  function backdrop(key, palette, size) {
    var S = size || 300, g = GEN[key];
    if (!g) return null;
    var out = g(palette, S);
    if (out && !out.content) out.content = { x: 0, y: 0, w: 1, h: 1 }; /* full-bleed default */
    return out;
  }

  var API = { deriveBackdropPalette: deriveBackdropPalette, listBackdrops: listBackdrops, backdrop: backdrop, BRAND: BRAND };
  if (typeof module !== 'undefined' && module.exports) module.exports = API;
  else root.DTBackdrops = API;
})(typeof window !== 'undefined' ? window : this);